import { ThemeComponent } from '@/theme/ThemeComponent';
import { BaseComponent, Component, Prop, Ref, SimpleEvent } from '@eastsideco/salvo-ts';
import { AutoresolvingOptionSelector, OptionSelector } from '@/utils/OptionSelectors';
import { ISimpleEvent } from 'strongly-typed-events';
import { ProductCard } from '@/components/ProductCard/ProductCard';

export interface ProductOption extends BaseComponent {
    selected: string|null;
    onChange: ISimpleEvent<string|null>;
    updateBasedOnSelector(selector: OptionSelector): void;
    select(value: string|null): void;
}

@Component
export class ProductOptionPicker extends ThemeComponent {
    @Prop id!: string;
    @Prop product!: Shopify.Liquid.Product;
    @Prop({default: false}) isFrameset!: boolean;

    @Ref option1!: ProductOption;
    @Ref({required: false}) option2?: ProductOption;
    @Ref({required: false}) option3?: ProductOption;

    selector: AutoresolvingOptionSelector|null = null;

    get selectedVariant(): Shopify.Liquid.Variant|null {
        const options = [
            this.option1.selected,
            this.option2?.selected || null,
            this.option3?.selected || null,
        ];
        const match = this.product.variants.filter((v) => v.option1 == options[0]
                && v.option2 == options[1]
                && v.option3 == options[2])[0];

        return match || null;
    }

    private  parentProductComponent = this.closest('product-card') as ProductCard;
    private _onSelectedVariantChange = new SimpleEvent<Shopify.Liquid.Variant|null>();
    public get onSelectedVariantChange() {
        return this._onSelectedVariantChange.asEvent();
    }

    init() {
        this.selector = new AutoresolvingOptionSelector(this.product);

        let oldValue = this.selectedVariant;
        this.renderOptions();

        const onAnyChanged = () => {
            const newValue = this.selectedVariant;
            if (newValue != oldValue) {
                this._onSelectedVariantChange.dispatch(newValue);
            }
            oldValue = newValue;
            this.renderOptions();
        };

        this.$subscribe(this.option1.onChange, onAnyChanged);
        if (this.option2) {
            this.$subscribe(this.option2.onChange, onAnyChanged);
        }
        if (this.option3) {
            this.$subscribe(this.option3.onChange, onAnyChanged);
        }

        this._onSelectedVariantChange.dispatch(this.selectedVariant);
        this.renderOptions();

        if(!this.parentProductComponent) { return; }
        this.$subscribe(this.parentProductComponent.onSelectedVariantChange, (variant) => {
            if (variant) {
                this.option1.selected = variant.option1;
                if (this.option2) {
                    this.option2.selected = variant.option2;
                }
                if (this.option3) {
                    this.option3.selected = variant.option3;
                }
                onAnyChanged();
                this._onSelectedVariantChange.dispatch(variant);
                this.renderOptions();
            }
        });
    }

    renderOptions() {
        if (!this.selector) {
            return;
        }
        this.selector.select(this.option1.selected, this.option2?.selected || null, this.option3?.selected || null);

        this.option1.updateBasedOnSelector(this.selector);
        this.option2?.updateBasedOnSelector(this.selector);
        this.option3?.updateBasedOnSelector(this.selector);

        // AutoresolvingOptionSelector may modify the values if
        if (this.selector.wasSelectionModified) {
            const selected = this.selector.selected;
            this.option1.select(selected[0].value);
            if (this.option2) {
                this.option2.select(selected[1].value);
            }
            if (this.option3) {
                this.option3.select(selected[2].value);
            }
            this._onSelectedVariantChange.dispatch(this.selectedVariant);
            this.renderOptions();
        }
    }
}
